import axios from 'axios';
import querystring from 'query-string';

import { apiConfig } from '@/api';
import { dataReviewApi } from '@/api/clients/dataReviewApi';
import { oauthApi } from '@/api/clients/oauthApi';
import {
  Configuration,
  DataReviewerResponse,
  type DataReviewerResponseReviewGroupsInner,
  OauthApi,
  UserAuthorizationResponse,
  UserAuthorizationResponseReasonEnum,
} from '@/api/generated/MNT';
import { config } from '@/config';

export type ReviewerAuthInfo = Exclude<DataReviewerResponse, 'id'> & {
  access_token: string;
  reviewer_id: number;
};

const _handleAuthReq = async (url: string, data: FormData): Promise<ReviewerAuthInfo> => {
  const response = await fetch(url, {
    method: 'POST',
    body: data,
  });

  if (response.status === 401) {
    throw new Error('Wrong email or password');
  } else if (response.status !== 200) {
    throw new Error(`${response.status}`);
  }

  const text = await response.text();
  const queryParams = querystring.parse(text.substr(1));
  const { access_token } = queryParams;
  if (!access_token || Array.isArray(access_token)) {
    throw new Error(`auth_failed`);
  }

  const res = await dataReviewApi.appApiDataReviewerGetCurrentDataReviewer();
  const reviewerInfo = res.data;

  return {
    access_token: access_token,
    reviewer_id: reviewerInfo.id,
    ...res.data,
    review_groups: reviewerInfo.review_groups?.filter((group: any) => group.is_active) || [],
  };
};

export const authenticateToken = async (token: string): Promise<ReviewerAuthInfo> => {
  const authUrl = `${config.API_URL}/api/oauth/authorize_auditor_token`;

  const formData = new FormData();
  formData.append('token', token);
  formData.append('client_id', '2');
  formData.append('redirect_uri', '/');
  formData.append('response_type', 'token');

  return _handleAuthReq(authUrl, formData);
};

/**
 * Thrown by ``authenticate`` when the credentials are valid but the account
 * requires MFA: the server has emailed a 6-digit code and the caller must
 * re-submit the same credentials with ``otp`` set. Accounts without MFA never
 * see this — they get a token on the first call.
 */
export class MfaRequiredError extends Error {
  constructor() {
    super('A verification code is required');
    this.name = 'MfaRequiredError';
  }
}

const authFailureMessage = (reason: string | undefined, otp: string | undefined): string => {
  switch (reason) {
    case UserAuthorizationResponseReasonEnum.TokenInvalid:
      return 'The code is incorrect or expired';
    case UserAuthorizationResponseReasonEnum.InactiveUser:
      return 'This account is inactive';
    case UserAuthorizationResponseReasonEnum.WrongRegion:
      return 'This account belongs to a different region';
    default:
      return otp ? 'Verification failed' : 'Wrong email or password';
  }
};

// Uses ``/oauth/authorize`` rather than ``/oauth/authorize_swagger``: the swagger
// variant has no OTP round trip and refuses MFA-required users outright
// [FOOD-980]. Same flow as the clinician portal and mnt-web-admin.
export const authenticate = async (
  email: string,
  password: string,
  otp?: string,
  oauth: OauthApi = oauthApi,
  apiConf: Configuration = apiConfig,
): Promise<ReviewerAuthInfo> => {
  let authData: UserAuthorizationResponse | undefined;
  try {
    const authResponse = await oauth.appApiOauthAuthorize({
      client_id: '2',
      username: email,
      password: password,
      redirect_uri: '/',
      response_type: 'token',
      token: otp || undefined,
    });
    authData = authResponse.data;
  } catch (e) {
    // 401/409 from /oauth/authorize carry a UserAuthorizationResponse body
    // (``mfa_token_required``, ``auth_failed``, ``token_invalid``, ...).
    // Anything else (400, 5xx, network) propagates as-is.
    if (
      !axios.isAxiosError(e)
      || (e.response?.status !== 401 && e.response?.status !== 409)
      || !e.response.data
      || typeof e.response.data !== 'object'
    ) {
      throw e;
    }
    authData = e.response.data as UserAuthorizationResponse;
  }

  if (authData?.reason === UserAuthorizationResponseReasonEnum.MfaTokenRequired) {
    throw new MfaRequiredError();
  }
  const accessToken = authData?.oauth_token?.access_token;
  if (!authData?.success || !accessToken) {
    throw new Error(authFailureMessage(authData?.reason, otp));
  }
  apiConf.accessToken = accessToken;

  const currentUser = await dataReviewApi.appApiDataReviewerGetCurrentDataReviewer();
  const reviewGroups = currentUser.data.review_groups || [];

  return {
    access_token: accessToken,
    reviewer_id: currentUser.data.id,
    ...currentUser.data,
    review_groups: reviewGroups.filter(group => group.is_active),
  };
};
